import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import get from "lodash-es/get";
import PropTypes from "prop-types";

// Actions
import * as injuryActions from "../../state/injuries/actions";
import * as physicalDamageActions from "../../state/physicalDamage/actions";

// Config
import { capturePages, sensitiveSections } from "../../config/values";
import { snackbarTypes } from "../../config/snackbar";
import { useSensitiveImage } from "../../context/SensitiveImageProvider";
import styles from "../../config/styles";

// Utils
import { generateFilename, getImageSrc, imageTypes } from "../../utils/images";
import { toggleSnack } from "../../state/snackbar/actions";
import { useAppInsights } from "../../utils/telemetry/AppInsights";

// Components
import AnnotationTool from "../../components/AnnotationTool";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import LoadingSection from "../../components/LoadingSection";
import NavigationController from "./NavigationController";

const useStyles = makeStyles(() => ({
  main: {
    ...styles.mixins.mainSection,
    marginBottom: styles.values.annotationFooterHeight,
    position: "relative"
  },
  canvas: {
    height: `calc(100vh - ${styles.values.headerHeight}px - ${styles.values.annotationFooterHeight}px)`,
    width: "auto",
    maxWidth: "100vw",
    borderLeft: styles.values.border,
    borderRight: styles.values.border,
    filter: ({ applyFilter }) => (applyFilter ? "blur(20px)" : "none")
  },
  hidden: {
    display: "none"
  }
}));

const texts = {
  delete: ["toolPage.deleteDialog.title", "toolPage.deleteDialog.description"],
  discard: [
    "toolPage.discardDialog.title",
    "toolPage.discardDialog.description"
  ]
};

const initialState = { status: false, type: null };

const annotable = [imageTypes.normalGrid, imageTypes.largeGrid];
const IMAGE_POLL_INTERVAL = 1000;
const MAX_HEADER_RETRIES = 5;
const HEADER_RETRY_DELAY = 1000;

const ToolPageWrapper = ({ currentImage, goBack, page }) => {
  const isInjury = page === capturePages.INJURY;
  const actions = isInjury ? injuryActions : physicalDamageActions;
  const idParamName = isInjury ? "injuryId" : "physicalDamageId";

  const [open, setOpen] = useState(initialState);
  // Once a filename's header fetch has settled for the first time, the
  // canvas stays open for good - a retry flips loadingHeaders back to true
  // for a moment, and gating render on its live value made the canvas
  // flicker shut/open on every retry instead of upgrading silently.
  const [settledFilenames, setSettledFilenames] = useState({});
  const { caseId, injuryId, physicalDamageId, imgIndex } = useParams();
  const itemId = isInjury ? injuryId : physicalDamageId;
  const { clickedThumbnail } = useSensitiveImage();
  const { trackException } = useAppInsights();
  const history = useHistory();
  const dispatch = useDispatch();
  const item = useSelector(isInjury ? s => s.injuries : s => s.physicalDamage);
  const hasFetchedImage = useRef({});
  const headerRetryCount = useRef({});

  // Physical damage has no body-part classification, so it has no sensitive
  // content to blur.
  const isSensitive =
    isInjury && sensitiveSections.includes(get(item, "data.bodySection", ""));

  const classes = useStyles({
    applyFilter: isSensitive && imgIndex !== clickedThumbnail
  });

  const imageHeaders = item.imageHeaders[currentImage.filename];
  const loadingHeaders = item.loadingHeaders[currentImage.filename];

  useEffect(() => {
    if (!item.isLoadingImage) {
      if (
        (currentImage.isProcessing ||
          (!currentImage.downloadURLs && !currentImage.resourceURLs)) &&
        !item.requestImageError
      ) {
        // Polls until the image finishes processing server-side. The first
        // attempt for a given file fires immediately; retries are spaced out
        // so a slow-to-process image doesn't hammer the API in a tight loop
        // (each successful-but-still-processing response replaces this image
        // with a new object, which would otherwise retrigger this effect
        // instantly).
        const alreadyFetched = hasFetchedImage.current[currentImage.filename];
        const timeoutId = setTimeout(
          () => {
            hasFetchedImage.current[currentImage.filename] = true;
            dispatch(
              actions.getImage({
                caseId,
                filename: currentImage.filename,
                index: imgIndex,
                [idParamName]: itemId
              })
            );
          },
          alreadyFetched ? IMAGE_POLL_INTERVAL : 0
        );
        return () => clearTimeout(timeoutId);
      }
      if (!getImageSrc(currentImage)) {
        dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
      } else if (annotable.includes(currentImage.imageType)) {
        // loadingHeaders is undefined before the first attempt, then
        // becomes (and stays) false once that attempt finishes - whether it
        // succeeded or not. That's the only reliable way to tell "never
        // tried" from "tried and got nothing back": imageHeaders is null in
        // both the not-yet-fetched and fetched-but-no-metadata cases.
        const fetchHeaders = () =>
          dispatch(
            actions.getHeaders({
              ...currentImage,
              onFail: () =>
                trackException(
                  `Failed to get image headers for image: ${currentImage.filename} at case: ${caseId}`
                )
            })
          );

        if (loadingHeaders === undefined) {
          headerRetryCount.current[currentImage.filename] = 1;
          fetchHeaders();
        } else if (loadingHeaders === false) {
          if (!settledFilenames[currentImage.filename]) {
            setSettledFilenames(prev => ({
              ...prev,
              [currentImage.filename]: true
            }));
          }

          if (!imageHeaders) {
            const attempt =
              headerRetryCount.current[currentImage.filename] || 0;

            if (attempt < MAX_HEADER_RETRIES) {
              // Scaling can genuinely take a moment to finish server-side -
              // retry a handful of times, spaced further apart each time,
              // before accepting there's no scale for this image.
              const timeoutId = setTimeout(() => {
                headerRetryCount.current[currentImage.filename] = attempt + 1;
                fetchHeaders();
              }, attempt * HEADER_RETRY_DELAY);
              return () => clearTimeout(timeoutId);
            }

            // No forensic scale for this image after all retries (scaling
            // failed, or it was never captured with a reference card) - show
            // an error but still let the tool open below with ruler/grid
            // disabled, rather than blocking the whole canvas.
            dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
          }
        }
      }
    }
  }, [
    actions,
    caseId,
    currentImage,
    dispatch,
    idParamName,
    imageHeaders,
    imgIndex,
    item.isLoadingImage,
    item.requestImageError,
    itemId,
    loadingHeaders,
    settledFilenames,
    trackException
  ]);

  const handleClickOpen = type => () => setOpen({ status: true, type });
  const handleClose = () => setOpen(initialState);

  const handleSave = image => {
    const filename = generateFilename(currentImage.filename, "annotated");
    dispatch(
      actions.uploadAnnotatedImage({
        caseId,
        description: currentImage.description,
        filename,
        image,
        imageMeta: imageHeaders,
        [idParamName]: itemId,
        onSuccess: goBack,
        onFail: msg =>
          trackException(
            `${msg} at file: ${filename} from ${idParamName}: ${itemId} in case: ${caseId}`
          )
      })
    );
  };

  const handleDelete = () => {
    dispatch(
      actions.deleteImage({
        caseId,
        [idParamName]: itemId,
        filename: currentImage.filename,
        onSuccess: goBack,
        onFail: () =>
          trackException(
            `Failed to delete file: ${currentImage.filename} from ${idParamName}: ${itemId} in case: ${caseId}`
          )
      })
    );
  };

  // history.back(), not the goBack prop - that's a fixed push to the detail
  // page, this should land on the previous image instead.
  const handleStillProcessing = () => {
    dispatch(
      toggleSnack({
        type: snackbarTypes.STILL_PROCESSING,
        text: "common.wip.stillProcessing"
      })
    );
    history.goBack();
  };

  return (
    <main>
      {getImageSrc(currentImage) &&
      (!annotable.includes(currentImage.imageType) ||
        settledFilenames[currentImage.filename]) ? (
        <>
          <NavigationController page={page}>
            <AnnotationTool
              key={imgIndex}
              classes={classes}
              image={currentImage}
              imageMeta={imageHeaders}
              index={imgIndex}
              isSensitive={isSensitive}
              onDelete={handleClickOpen("delete")}
              onDiscard={handleClickOpen("discard")}
              onSave={handleSave}
              onStillProcessing={handleStillProcessing}
              page={page}
            />
          </NavigationController>
          <ConfirmationDialog
            onClose={handleClose}
            open={open.status}
            onConfirm={open.type === "delete" ? handleDelete : goBack}
            textKeys={texts[open.type]}
            isLoading={!!item.isDeletingImage}
            type={open.type}
          />
        </>
      ) : (
        <LoadingSection />
      )}
    </main>
  );
};

ToolPageWrapper.propTypes = {
  currentImage: PropTypes.shape({
    description: PropTypes.string,
    filename: PropTypes.string,
    imageType: PropTypes.string,
    isProcessing: PropTypes.bool.isRequired,
    downloadURLs: PropTypes.shape({
      webLargePreviewURL: PropTypes.string
    }),
    resourceURLs: PropTypes.shape({
      image: PropTypes.string
    })
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  page: PropTypes.oneOf(Object.values(capturePages)).isRequired
};

export default ToolPageWrapper;
